import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import GoBackButton from '@/common/components/GoBackButton';
import { useDocumentOptions } from '@/common/hooks/queries/useDocumentOptions';
import { useGoBack } from '@/common/hooks/useGoBack'
import { FormTransformers } from '@/common/utils/formTransformers'
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist'
import { ContactDocumentsSection } from '@/features/publicCompanyLeadsFunnel/components/ContactDocumentsSection'
import { ContactPersonalInformationSection } from '@/features/publicCompanyLeadsFunnel/components/ContactPersonalInformationSection'
import { stateStrategy } from '@/features/publicCompanyLeadsFunnel/constants/stateStrategy.constant'
import { useAdvancePublicCompanyLeadContact } from '@/features/publicCompanyLeadsFunnel/hooks/useAdvancePublicCompanyLeadContact'
import { useDeletePublicCompanyLeadContact } from '@/features/publicCompanyLeadsFunnel/hooks/useDeletePublicCompanyLeadContact'
import { useRegressContact } from '@/features/publicCompanyLeadsFunnel/hooks/useRegressContact'
import { useReviewContact } from '@/features/publicCompanyLeadsFunnel/hooks/useReviewContact'
import { useReviewContactDocument } from '@/features/publicCompanyLeadsFunnel/hooks/useReviewContactDocument'
import { useToggleContactDocument } from '@/features/publicCompanyLeadsFunnel/hooks/useToggleContactDocument'
import { useUpdatePublicCompanyLeadContact } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadContact'
import {
  PublicCompanyLeadsFunnelUpdateContactValidationSchema,
  updateContactSchema,
} from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { leadContactDocumentsDefaultValues } from '@/features/publicCompanyLeadsFunnel/services/leadContactDocumentDefaultValues';
import { ContactData, ContactState } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios';
import { ChevronsRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

const Form = ({
  contact,
  publicCompanyLeadState,
  publicCompanyLeadName,
}: {
  contact?: ContactData
  publicCompanyLeadState: keyof typeof stateStrategy
  publicCompanyRestContacts: ContactData[]
  publicCompanyLeadName: string
}) => {
  const { publicCompanyLeadId } = useParams()
  const isInContracting = publicCompanyLeadState === 'contracting'
  const passedEveryRequirement = contact?.requirement_list_summary?.steps?.every((step) => step.passed)
  const hasDisapprovedReview =
    contact?.last_review?.state === 'disapproved' ||
    contact?.documents?.some((doc) => doc.last_review?.state === 'disapproved')
  const goBack = useGoBack()
  const [editContact, setEditContact] = useState(false)
  const [documentToModal, setDocumentToModal] = useState('')
  const [files, setFiles] = useState<{ id: string; file: Blob; fileName: string }[]>([])
  const [serverError, setServerError] = useState(false)
  const queryClient = useQueryClient()

  const { data: documentOptions, isFetched: isDocumentOptionsFetched } = useDocumentOptions();

  const updateLeadOnSuccess = (response: AxiosResponse) => {
    editContact && setEditContact(false)
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })

    const contactDocuments = (response.data.data.contacts as ContactData[]).find(({ id }) => id === (contact?.id || ''))?.documents;

    setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contactDocuments) as any);
  }

  const onToggleDocumentSuccess = (response: AxiosResponse) => {
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] });

    const contactDocuments = (response.data.data.contacts as ContactData[]).find(({ id }) => id === (contact?.id || ''))?.documents;

    setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contactDocuments) as any);
  }

  const redirectOnSuccess = () => {
    goBack({ fallback: `/public-clients/${publicCompanyLeadId}/edit` })
  }

  const onError = () => setServerError(true)

  const { mutate: toggleDocument } = useToggleContactDocument({
    contactId: contact?.id,
    publicCompanyLeadId,
    onSuccess: onToggleDocumentSuccess,
    onError,
  })

  const { mutate: deletePublicCompanyLeadContact } = useDeletePublicCompanyLeadContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
    onError,
  })

  const { mutate: regressContact } = useRegressContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
    onError,
  })

  const { mutate: advancePublicCompanyLeadContact } = useAdvancePublicCompanyLeadContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
    onError,
  })

  const { mutate: approveContact } = useReviewContact({
    contactId: contact?.id,
    publicCompanyLeadId,
    state: 'approved',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: declineContact } = useReviewContact({
    contactId: contact?.id,
    publicCompanyLeadId,
    state: 'disapproved',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: approveContactDocument } = useReviewContactDocument({
    contactId: contact?.id,
    publicCompanyLeadId,
    documentId: documentToModal,
    state: 'approved',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: declineContactDocument } = useReviewContactDocument({
    contactId: contact?.id,
    publicCompanyLeadId,
    documentId: documentToModal,
    state: 'disapproved',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const useFormMethods = useForm<PublicCompanyLeadsFunnelUpdateContactValidationSchema>({
    resolver: zodResolver(updateContactSchema),
    defaultValues: {
      contacts: {
        full_name: contact?.full_name,
        email: contact?.email,
        role_in_company: contact?.role_in_company,
        phone: {
          number: contact?.phone?.number,
        },
        documents: leadContactDocumentsDefaultValues(documentOptions, contact?.documents),
      },
    },
  })

  const { handleSubmit, reset, setError, setValue } = useFormMethods

  const { mutate: updateContact, isPending: updateContactIsPending } = useUpdatePublicCompanyLeadContact({
    publicCompanyLeadId,
    contactId: contact?.id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateContactValidationSchema>({ setError }),
  })

  const onSubmit: SubmitHandler<PublicCompanyLeadsFunnelUpdateContactValidationSchema> = (data) => {
    contact?.documents?.forEach((document, index) => {
      if (document.id && data.contacts.documents[index]) {
        data.contacts.documents[index]!.id = document.id
        const file = files.find((file) => file.id === document.id)

        if (file) {
          data.contacts.documents[index]!.file = files.find((file) => file.id === document.id)?.file
        }
      }
    })

    updateContact(data)
  }
  const submit = handleSubmit(onSubmit)

  useEffect(() => {
    if (isDocumentOptionsFetched) {
      setValue('contacts.documents', leadContactDocumentsDefaultValues(documentOptions, contact?.documents) as any);
    }
  }, [isDocumentOptionsFetched, contact?.documents?.[0]?.file?.id]);

  return (
    <FormProvider {...useFormMethods}>
      <form>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/public-clients/${publicCompanyLeadId}/edit`} />

            <p className="text-white-400 font-normal">{publicCompanyLeadName}</p>
            <ChevronsRight color="#ffffffb3" />
            <h2>{contact?.full_name || 'Novo contato'}</h2>
          </div>
          <div className="flex gap-4">
            {contact?.state === ContactState.pending && (
              <>
                <Button
                  type="button"
                  variant="outline-error"
                  onClick={() => {
                    if (editContact) {
                      reset()
                      setFiles([])
                      setEditContact(false)
                      setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contact.documents) as any);
                    } else {
                      deletePublicCompanyLeadContact()
                    }
                  }}
                  disabled={updateContactIsPending}
                >
                  {editContact ? 'Cancelar' : 'Deletar'}
                </Button>
                {!editContact ? (
                  <Button
                    type="button"
                    onClick={() => setEditContact(true)}
                    name="edit"
                  >
                    Editar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={() => submit()}
                    disabled={updateContactIsPending}
                    name="confirm"
                  >
                    Confirmar
                  </Button>
                )}
              </>
            )}
            {contact?.state === ContactState.pending && (
              <Button
                type="button"
                disabled={!passedEveryRequirement || updateContactIsPending || editContact}
                variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                onClick={() => advancePublicCompanyLeadContact()}
                name="advance_lead_contact"
              >
                Enviar solicitação
              </Button>
            )}
            {contact?.state === ContactState.in_review && isInContracting && (
              <>
                <Button
                  type="button"
                  disabled={!hasDisapprovedReview}
                  variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                  onClick={() => regressContact()}
                >
                  Solicitar revisão
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-6 w-full">
          <Box className="flex w-full flex-col gap-4 max-h-[calc(100vh-162px)] overflow-y-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            {serverError && (
              <p className="text-sm text-danger-soft">
                Ocorreu um erro no servidor, cheque os campos e tente novamente
              </p>
            )}
            <ContactPersonalInformationSection
              publicCompanyLeadId={publicCompanyLeadId}
              contact={contact}
              showReviewButtons={contact?.state === ContactState.in_review && isInContracting}
              isPending={updateContactIsPending}
              approveContact={approveContact}
              declineContact={declineContact}
              disableFields={!editContact || updateContactIsPending}
            />
            <ContactDocumentsSection
              contact={contact}
              files={files}
              setFiles={setFiles}
              setDocumentToModal={setDocumentToModal}
              showReviewButtons={contact?.state === ContactState.in_review && isInContracting}
              approveContactDocument={approveContactDocument}
              declineContactDocument={declineContactDocument}
              toggleDocument={toggleDocument}
              editContact={editContact}
              updateContactIsPending={updateContactIsPending}
            />
          </Box>
          <Checklist
            requirement_list_summary={isInContracting ? contact?.requirement_list_summary : undefined}
            className="flex flex-col gap-2 justify-between h-full max-h-[calc(100vh-170px)]"
            Buttons={() => (
              <div className="flex flex-col w-full gap-2 mt-auto mb-[72px]">
                {contact?.state === ContactState.pending && (
                  <Button
                    type="button"
                    disabled={!passedEveryRequirement || updateContactIsPending || editContact}
                    variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                    onClick={() => advancePublicCompanyLeadContact()}
                  >
                    Enviar solicitação
                  </Button>
                )}
                {contact?.state === ContactState.awaiting_client_documents && (
                  <Button type="button" disabled variant="outline-disabled">
                    Aguardando
                  </Button>
                )}
                {contact?.state === ContactState.in_review && isInContracting && (
                  <>
                    <Button
                      type="button"
                      disabled={!hasDisapprovedReview}
                      variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                      onClick={() => regressContact()}
                    >
                      Solicitar revisão
                    </Button>
                  </>
                )}
              </div>
            )}
          />
        </div>
      </form>
    </FormProvider>
  )
}

export default Form
